import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  MapPin,
  Hash,
  Save,
  Lock,
  Info,
  Building,
  Search,
  Filter,
  CheckSquare,
  Square,
  ChevronLeft,
  ChevronRight,
  Zap,
  ClipboardList,
  Printer,
  PackageCheck,
  Weight,
  Ruler,
  DollarSign,
  Globe,
  Phone,
  Mail,
  Box,
  Boxes,
  ScanLine,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Types
interface PickedOrderLine {
  id: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityPicked: number;
  quantityShipped: number;
  quantityToShip: number;
  unitPrice: number;
  lineTotal: number;
  warehouseCode: string;
  warehouseName: string;
  locationCode: string;
  locationName: string;
}

interface PickedOrder {
  id: string;
  soNumber: string;
  pickReference: string;
  customerCode: string;
  customerName: string;
  customerContact: string;
  customerEmail: string;
  customerPhone: string;
  orderDate: Date;
  pickedDate: Date;
  pickedBy: string;
  deliveryAddress: {
    line1: string;
    line2?: string;
    city: string;
    postcode: string;
    country: string;
  };
  status: "ready-to-ship" | "partially-shipped" | "shipped";
  totalValue: number;
  currency: string;
  lines: PickedOrderLine[];
  notes: string;
}

interface ShippingLine {
  pickLineId: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  quantityToShip: number;
  quantityShipped: number;
  isSelected: boolean;
  packageNumber?: number;
}

interface ShippingPackage {
  packageNumber: number;
  weight: number;
  weightUnit: "kg" | "lbs";
  length: number;
  width: number;
  height: number;
  dimensionUnit: "cm" | "in";
  trackingNumber: string;
  items: {
    lineNumber: number;
    stockItemCode: string;
    quantity: number;
  }[];
}

// Mock data for picked orders ready to ship
const MOCK_PICKED_ORDERS: PickedOrder[] = [
  {
    id: "1",
    soNumber: "SO-2024-001",
    pickReference: "PICK-2024-001",
    customerCode: "CUST-001",
    customerName: "ABC Retail Ltd",
    customerContact: "Jane Smith",
    customerEmail: "jane@abcretail.com",
    customerPhone: "+44 20 1234 5678",
    orderDate: new Date("2024-01-15"),
    pickedDate: new Date("2024-01-20"),
    pickedBy: "John Doe",
    deliveryAddress: {
      line1: "123 High Street",
      line2: "Suite 100",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    status: "ready-to-ship",
    totalValue: 2450.00,
    currency: "GBP",
    notes: "Handle with care - fragile items",
    lines: [
      {
        id: "1",
        lineNumber: 1,
        stockItemCode: "WIDGET-001",
        stockItemName: "Premium Widget",
        description: "High-quality widget for industrial use",
        quantityPicked: 10,
        quantityShipped: 0,
        quantityToShip: 10,
        unitPrice: 125.00,
        lineTotal: 1250.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse",
        locationCode: "A-01-01",
        locationName: "Aisle A, Rack 01, Shelf 01"
      },
      {
        id: "2",
        lineNumber: 2,
        stockItemCode: "GADGET-002",
        stockItemName: "Electronic Gadget",
        description: "Latest model electronic gadget",
        quantityPicked: 5,
        quantityShipped: 0,
        quantityToShip: 5,
        unitPrice: 240.00,
        lineTotal: 1200.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse",
        locationCode: "B-02-03",
        locationName: "Aisle B, Rack 02, Shelf 03"
      }
    ]
  },
  {
    id: "2",
    soNumber: "SO-2024-002",
    pickReference: "PICK-2024-002",
    customerCode: "CUST-002",
    customerName: "XYZ Manufacturing",
    customerContact: "Bob Johnson",
    customerEmail: "bob@xyzmanufacturing.com",
    customerPhone: "+44 161 555 0123",
    orderDate: new Date("2024-01-16"),
    pickedDate: new Date("2024-01-21"),
    pickedBy: "Sarah Williams",
    deliveryAddress: {
      line1: "456 Industrial Park",
      city: "Manchester",
      postcode: "M1 1AA",
      country: "United Kingdom"
    },
    status: "ready-to-ship",
    totalValue: 3750.00,
    currency: "GBP",
    notes: "",
    lines: [
      {
        id: "3",
        lineNumber: 1,
        stockItemCode: "TOOL-003",
        stockItemName: "Professional Tool Set",
        description: "Complete professional tool set",
        quantityPicked: 15,
        quantityShipped: 0,
        quantityToShip: 15,
        unitPrice: 250.00,
        lineTotal: 3750.00,
        warehouseCode: "WH-NORTH",
        warehouseName: "North Warehouse",
        locationCode: "C-01-02",
        locationName: "Aisle C, Rack 01, Shelf 02"
      }
    ]
  },
  {
    id: "3",
    soNumber: "SO-2024-003",
    pickReference: "PICK-2024-003",
    customerCode: "CUST-003",
    customerName: "Tech Solutions Inc",
    customerContact: "Alice Brown",
    customerEmail: "alice@techsolutions.com",
    customerPhone: "+44 121 555 9876",
    orderDate: new Date("2024-01-17"),
    pickedDate: new Date("2024-01-22"),
    pickedBy: "Mike Davis",
    deliveryAddress: {
      line1: "789 Tech Boulevard",
      line2: "Building 3",
      city: "Birmingham",
      postcode: "B1 1AA",
      country: "United Kingdom"
    },
    status: "partially-shipped",
    totalValue: 5200.00,
    currency: "GBP",
    notes: "Urgent delivery required",
    lines: [
      {
        id: "4",
        lineNumber: 1,
        stockItemCode: "COMP-004",
        stockItemName: "Computer Component",
        description: "High-performance computer component",
        quantityPicked: 20,
        quantityShipped: 10,
        quantityToShip: 10,
        unitPrice: 260.00,
        lineTotal: 5200.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse",
        locationCode: "D-03-01",
        locationName: "Aisle D, Rack 03, Shelf 01"
      }
    ]
  }
];

// Mock shipping carriers
const SHIPPING_CARRIERS = [
  { code: "DHL", name: "DHL Express" },
  { code: "FEDEX", name: "FedEx International" },
  { code: "UPS", name: "UPS Worldwide" },
  { code: "ROYAL", name: "Royal Mail" },
  { code: "DPD", name: "DPD UK" },
  { code: "HERMES", name: "Hermes" },
  { code: "YODEL", name: "Yodel" },
  { code: "PARCELFORCE", name: "Parcelforce" }
];

// Mock shipping services
const SHIPPING_SERVICES = [
  { code: "STANDARD", name: "Standard Delivery (3-5 days)", carrier: "DHL" },
  { code: "EXPRESS", name: "Express Delivery (1-2 days)", carrier: "DHL" },
  { code: "OVERNIGHT", name: "Overnight Delivery", carrier: "DHL" },
  { code: "ECONOMY", name: "Economy Delivery (5-7 days)", carrier: "FEDEX" },
  { code: "PRIORITY", name: "Priority Delivery (2-3 days)", carrier: "FEDEX" },
  { code: "NEXT_DAY", name: "Next Day Delivery", carrier: "UPS" },
  { code: "2ND_CLASS", name: "2nd Class Post", carrier: "ROYAL" },
  { code: "1ST_CLASS", name: "1st Class Post", carrier: "ROYAL" }
];

export default function ShippingPage() {
  const { navigateTo } = useNavigation();

  // State for selected order
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");
  const [selectedOrder, setSelectedOrder] = useState<PickedOrder | null>(null);

  // State for shipping details
  const [shipmentDate, setShipmentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [shipmentReference, setShipmentReference] = useState<string>("");
  const [carrier, setCarrier] = useState<string>("");
  const [service, setService] = useState<string>("");
  const [shippedBy, setShippedBy] = useState<string>("Current User");
  const [shippingNotes, setShippingNotes] = useState<string>("");

  // State for shipping lines
  const [shippingLines, setShippingLines] = useState<ShippingLine[]>([]);

  // State for packages
  const [packages, setPackages] = useState<ShippingPackage[]>([]);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState<ShippingPackage | null>(null);

  // State for package form
  const [packageWeight, setPackageWeight] = useState<string>("");
  const [packageWeightUnit, setPackageWeightUnit] = useState<"kg" | "lbs">("kg");
  const [packageLength, setPackageLength] = useState<string>("");
  const [packageWidth, setPackageWidth] = useState<string>("");
  const [packageHeight, setPackageHeight] = useState<string>("");
  const [packageDimensionUnit, setPackageDimensionUnit] = useState<"cm" | "in">("cm");
  const [packageTrackingNumber, setPackageTrackingNumber] = useState<string>("");

  // State for UI
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQuickSetup, setShowQuickSetup] = useState(false);

  const itemsPerPage = 50;

  // Load order when selected
  useEffect(() => {
    if (selectedOrderId) {
      const order = MOCK_PICKED_ORDERS.find(o => o.id === selectedOrderId);
      if (order) {
        setSelectedOrder(order);
        
        // Initialize shipping lines
        const lines: ShippingLine[] = order.lines.map(line => ({
          pickLineId: line.id,
          lineNumber: line.lineNumber,
          stockItemCode: line.stockItemCode,
          stockItemName: line.stockItemName,
          quantityToShip: line.quantityToShip,
          quantityShipped: 0,
          isSelected: line.quantityToShip > 0,
          packageNumber: undefined
        }));
        setShippingLines(lines);

        // Set default reference
        setShipmentReference(`SHIP-${order.soNumber}-${Date.now()}`);
        setShippingNotes(order.notes);
      }
    } else {
      setSelectedOrder(null);
      setShippingLines([]);
      setPackages([]);
    }
  }, [selectedOrderId]);

  // Filter available services based on selected carrier
  const availableServices = useMemo(() => {
    if (!carrier) return [];
    return SHIPPING_SERVICES.filter(s => s.carrier === carrier);
  }, [carrier]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const selectedLines = shippingLines.filter(line => line.isSelected);
    const totalItems = selectedLines.reduce((sum, line) => sum + line.quantityToShip, 0);
    const totalLines = selectedLines.length;
    const totalPackages = packages.length;
    const totalWeight = packages.reduce((sum, pkg) => sum + pkg.weight, 0);

    return {
      totalItems,
      totalLines,
      totalPackages,
      totalWeight
    };
  }, [shippingLines, packages]);

  // Filter and paginate shipping lines
  const filteredLines = useMemo(() => {
    return shippingLines.filter(line => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        line.stockItemCode.toLowerCase().includes(search) ||
        line.stockItemName.toLowerCase().includes(search) ||
        line.lineNumber.toString().includes(search)
      );
    });
  }, [shippingLines, searchTerm]);

  const paginatedLines = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredLines.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLines, currentPage]);

  const totalPages = Math.ceil(filteredLines.length / itemsPerPage);

  // Handlers
  const handleSelectAll = () => {
    setShippingLines(lines => lines.map(line => ({ ...line, isSelected: true })));
  };

  const handleDeselectAll = () => {
    setShippingLines(lines => lines.map(line => ({ ...line, isSelected: false })));
  };

  const handleLineSelectionChange = (pickLineId: string, isSelected: boolean) => {
    setShippingLines(lines =>
      lines.map(line =>
        line.pickLineId === pickLineId ? { ...line, isSelected } : line
      )
    );
  };

  const handleQuantityChange = (pickLineId: string, value: string) => {
    const quantity = parseInt(value) || 0;
    setShippingLines(lines =>
      lines.map(line => {
        if (line.pickLineId === pickLineId) {
          const originalLine = selectedOrder?.lines.find(l => l.id === pickLineId);
          const maxQuantity = originalLine ? originalLine.quantityToShip : 0;
          return {
            ...line,
            quantityShipped: Math.min(Math.max(0, quantity), maxQuantity)
          };
        }
        return line;
      })
    );
  };

  const handleFillToMax = () => {
    setShippingLines(lines =>
      lines.map(line => {
        if (line.isSelected) {
          return { ...line, quantityShipped: line.quantityToShip };
        }
        return line;
      })
    );
  };

  const handleAddPackage = () => {
    setEditingPackage(null);
    setPackageWeight("");
    setPackageWeightUnit("kg");
    setPackageLength("");
    setPackageWidth("");
    setPackageHeight("");
    setPackageDimensionUnit("cm");
    setPackageTrackingNumber("");
    setShowPackageDialog(true);
  };

  const handleEditPackage = (pkg: ShippingPackage) => {
    setEditingPackage(pkg);
    setPackageWeight(pkg.weight.toString());
    setPackageWeightUnit(pkg.weightUnit);
    setPackageLength(pkg.length.toString());
    setPackageWidth(pkg.width.toString());
    setPackageHeight(pkg.height.toString());
    setPackageDimensionUnit(pkg.dimensionUnit);
    setPackageTrackingNumber(pkg.trackingNumber);
    setShowPackageDialog(true);
  };

  const handleSavePackage = () => {
    const weight = parseFloat(packageWeight) || 0;
    const length = parseFloat(packageLength) || 0;
    const width = parseFloat(packageWidth) || 0;
    const height = parseFloat(packageHeight) || 0;

    if (weight <= 0) {
      toast.error("Please enter a valid weight");
      return;
    }

    if (editingPackage) {
      // Update existing package
      setPackages(pkgs =>
        pkgs.map(pkg =>
          pkg.packageNumber === editingPackage.packageNumber
            ? {
                ...pkg,
                weight,
                weightUnit: packageWeightUnit,
                length,
                width,
                height,
                dimensionUnit: packageDimensionUnit,
                trackingNumber: packageTrackingNumber
              }
            : pkg
        )
      );
      toast.success("Package updated successfully");
    } else {
      // Add new package
      const newPackage: ShippingPackage = {
        packageNumber: packages.length + 1,
        weight,
        weightUnit: packageWeightUnit,
        length,
        width,
        height,
        dimensionUnit: packageDimensionUnit,
        trackingNumber: packageTrackingNumber,
        items: []
      };
      setPackages([...packages, newPackage]);
      toast.success("Package added successfully");
    }

    setShowPackageDialog(false);
  };

  const handleDeletePackage = (packageNumber: number) => {
    setPackages(pkgs => pkgs.filter(pkg => pkg.packageNumber !== packageNumber));
    // Remove package assignments from lines
    setShippingLines(lines =>
      lines.map(line =>
        line.packageNumber === packageNumber
          ? { ...line, packageNumber: undefined }
          : line
      )
    );
    toast.success("Package deleted successfully");
  };

  const handleAssignToPackage = (pickLineId: string, packageNumber: number | undefined) => {
    setShippingLines(lines =>
      lines.map(line =>
        line.pickLineId === pickLineId ? { ...line, packageNumber } : line
      )
    );
  };

  const handleQuickSetup = () => {
    setShowQuickSetup(true);
  };

  const handleApplyQuickSetup = (setupCarrier: string, setupService: string) => {
    setCarrier(setupCarrier);
    setService(setupService);
    setShowQuickSetup(false);
    toast.success("Shipping details applied to all items");
  };

  const validateShipment = (): string[] => {
    const errors: string[] = [];

    if (!selectedOrder) {
      errors.push("Please select an order to ship");
    }

    if (!shipmentDate) {
      errors.push("Shipment date is required");
    }

    if (!shipmentReference.trim()) {
      errors.push("Shipment reference is required");
    }

    if (!carrier) {
      errors.push("Shipping carrier is required");
    }

    if (!service) {
      errors.push("Shipping service is required");
    }

    const selectedLines = shippingLines.filter(line => line.isSelected);
    if (selectedLines.length === 0) {
      errors.push("Please select at least one item to ship");
    }

    selectedLines.forEach(line => {
      if (line.quantityShipped <= 0) {
        errors.push(`Line ${line.lineNumber}: Quantity to ship must be greater than 0`);
      }
      if (line.quantityShipped > line.quantityToShip) {
        errors.push(`Line ${line.lineNumber}: Quantity to ship cannot exceed available quantity`);
      }
    });

    if (packages.length === 0) {
      errors.push("Please add at least one package");
    }

    packages.forEach(pkg => {
      if (!pkg.trackingNumber.trim()) {
        errors.push(`Package ${pkg.packageNumber}: Tracking number is required`);
      }
    });

    return errors;
  };

  const handleSubmit = () => {
    const errors = validateShipment();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmShipment = async () => {
    setIsSubmitting(true);
    setShowConfirmDialog(false);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      const selectedLines = shippingLines.filter(line => line.isSelected);
      const totalItems = selectedLines.reduce((sum, line) => sum + line.quantityShipped, 0);

      toast.success(
        `Shipment created successfully! ${totalItems} items shipped for ${selectedOrder?.soNumber}`,
        {
          description: `Reference: ${shipmentReference}`
        }
      );

      // Navigate back to sales orders
      navigateTo("sales-orders");
    } catch (error) {
      toast.error("Failed to create shipment", {
        description: "Please try again or contact support"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintPackingSlip = () => {
    toast.info("Printing packing slip...", {
      description: "This feature will be implemented with backend integration"
    });
  };

  const handlePrintShippingLabel = () => {
    toast.info("Printing shipping labels...", {
      description: "This feature will be implemented with carrier API integration"
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ready-to-ship":
        return <Badge variant="default" className="bg-blue-500">Ready to Ship</Badge>;
      case "partially-shipped":
        return <Badge variant="default" className="bg-yellow-500">Partially Shipped</Badge>;
      case "shipped":
        return <Badge variant="default" className="bg-green-500">Shipped</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateTo("sales-orders")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Shipping</h1>
            <p className="text-muted-foreground">
              Create shipments for picked orders
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handlePrintPackingSlip}
            disabled={!selectedOrder}
          >
            <Printer className="mr-2 h-4 w-4" />
            Packing Slip
          </Button>
          <Button
            variant="outline"
            onClick={handlePrintShippingLabel}
            disabled={packages.length === 0}
          >
            <Printer className="mr-2 h-4 w-4" />
            Shipping Labels
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {selectedOrder && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StaticMetricCardAlt
            title="Items to Ship"
            value={summaryMetrics.totalItems.toString()}
            icon={Package}
            description={`${summaryMetrics.totalLines} line${summaryMetrics.totalLines !== 1 ? 's' : ''}`}
          />
          <StaticMetricCardAlt
            title="Total Packages"
            value={summaryMetrics.totalPackages.toString()}
            icon={Boxes}
            description="Packages configured"
          />
          <StaticMetricCardAlt
            title="Total Weight"
            value={`${summaryMetrics.totalWeight.toFixed(2)} kg`}
            icon={Weight}
            description="Combined weight"
          />
          <StaticMetricCardAlt
            title="Order Value"
            value={`£${selectedOrder.totalValue.toFixed(2)}`}
            icon={DollarSign}
            description={selectedOrder.currency}
          />
        </div>
      )}

      {/* Order Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Select Order
          </CardTitle>
          <CardDescription>
            Choose a picked order that is ready to ship
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="order-select">Picked Order *</Label>
              <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                <SelectTrigger id="order-select">
                  <SelectValue placeholder="Select a picked order..." />
                </SelectTrigger>
                <SelectContent>
                  {MOCK_PICKED_ORDERS.map(order => (
                    <SelectItem key={order.id} value={order.id}>
                      {order.soNumber} - {order.customerName} ({order.pickReference})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedOrder && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Order Information</AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-3 w-3" />
                    <span className="font-medium">Customer:</span> {selectedOrder.customerName} ({selectedOrder.customerContact})
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    <span className="font-medium">Email:</span> {selectedOrder.customerEmail}
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-3 w-3" />
                    <span className="font-medium">Phone:</span> {selectedOrder.customerPhone}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3 w-3" />
                    <span className="font-medium">Delivery:</span> {selectedOrder.deliveryAddress.line1}, {selectedOrder.deliveryAddress.city}, {selectedOrder.deliveryAddress.postcode}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3 w-3" />
                    <span className="font-medium">Picked:</span> {selectedOrder.pickedDate.toLocaleDateString()} by {selectedOrder.pickedBy}
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="h-3 w-3" />
                    <span className="font-medium">Status:</span> {getStatusBadge(selectedOrder.status)}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {selectedOrder && (
        <>
          {/* Shipping Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Shipping Details
              </CardTitle>
              <CardDescription>
                Enter shipment information and carrier details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="shipment-date">
                    <Calendar className="inline h-3 w-3 mr-1" />
                    Shipment Date *
                  </Label>
                  <Input
                    id="shipment-date"
                    type="date"
                    value={shipmentDate}
                    onChange={(e) => setShipmentDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shipment-reference">
                    <Hash className="inline h-3 w-3 mr-1" />
                    Shipment Reference *
                  </Label>
                  <Input
                    id="shipment-reference"
                    placeholder="e.g., SHIP-2024-001"
                    value={shipmentReference}
                    onChange={(e) => setShipmentReference(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shipped-by">
                    <User className="inline h-3 w-3 mr-1" />
                    Shipped By
                  </Label>
                  <Input
                    id="shipped-by"
                    placeholder="Enter name"
                    value={shippedBy}
                    onChange={(e) => setShippedBy(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="carrier">
                    <Truck className="inline h-3 w-3 mr-1" />
                    Carrier *
                  </Label>
                  <Select value={carrier} onValueChange={setCarrier}>
                    <SelectTrigger id="carrier">
                      <SelectValue placeholder="Select carrier..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIPPING_CARRIERS.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service">
                    <Package className="inline h-3 w-3 mr-1" />
                    Service *
                  </Label>
                  <Select value={service} onValueChange={setService} disabled={!carrier}>
                    <SelectTrigger id="service">
                      <SelectValue placeholder="Select service..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableServices.map(s => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="shipping-notes">
                  <FileText className="inline h-3 w-3 mr-1" />
                  Shipping Notes
                </Label>
                <Textarea
                  id="shipping-notes"
                  placeholder="Enter any special shipping instructions..."
                  value={shippingNotes}
                  onChange={(e) => setShippingNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleQuickSetup}
                >
                  <Zap className="mr-2 h-4 w-4" />
                  Quick Setup
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Packages */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Boxes className="h-5 w-5" />
                    Packages
                  </CardTitle>
                  <CardDescription>
                    Configure packages for this shipment
                  </CardDescription>
                </div>
                <Button onClick={handleAddPackage}>
                  <Box className="mr-2 h-4 w-4" />
                  Add Package
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {packages.length === 0 ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>No packages configured</AlertTitle>
                  <AlertDescription>
                    Add at least one package to continue with the shipment.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  {packages.map(pkg => (
                    <Card key={pkg.packageNumber}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">Package {pkg.packageNumber}</Badge>
                              {pkg.trackingNumber && (
                                <Badge variant="secondary">{pkg.trackingNumber}</Badge>
                              )}
                            </div>
                            <div className="grid gap-2 text-sm md:grid-cols-2">
                              <div className="flex items-center gap-2">
                                <Weight className="h-3 w-3 text-muted-foreground" />
                                <span>Weight: {pkg.weight} {pkg.weightUnit}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Ruler className="h-3 w-3 text-muted-foreground" />
                                <span>Dimensions: {pkg.length} × {pkg.width} × {pkg.height} {pkg.dimensionUnit}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditPackage(pkg)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeletePackage(pkg.packageNumber)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Items to Ship */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Items to Ship
                  </CardTitle>
                  <CardDescription>
                    Select items and quantities to include in this shipment
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    <CheckSquare className="mr-2 h-4 w-4" />
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                    <Square className="mr-2 h-4 w-4" />
                    Deselect All
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleFillToMax}>
                    <Zap className="mr-2 h-4 w-4" />
                    Fill to Max
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={shippingLines.every(line => line.isSelected)}
                          onCheckedChange={(checked) => {
                            if (checked) handleSelectAll();
                            else handleDeselectAll();
                          }}
                        />
                      </TableHead>
                      <TableHead>Line</TableHead>
                      <TableHead>Stock Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="text-right">To Ship</TableHead>
                      <TableHead>Package</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedLines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No items found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedLines.map((line) => {
                        const originalLine = selectedOrder.lines.find(l => l.id === line.pickLineId);
                        const isShipped = line.quantityShipped > 0;

                        return (
                          <TableRow
                            key={line.pickLineId}
                            className={isShipped ? "bg-green-50" : ""}
                          >
                            <TableCell>
                              <Checkbox
                                checked={line.isSelected}
                                onCheckedChange={(checked) =>
                                  handleLineSelectionChange(line.pickLineId, checked as boolean)
                                }
                              />
                            </TableCell>
                            <TableCell>{line.lineNumber}</TableCell>
                            <TableCell className="font-medium">{line.stockItemCode}</TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">{line.stockItemName}</div>
                                {originalLine && (
                                  <div className="text-xs text-muted-foreground">
                                    {originalLine.warehouseName} - {originalLine.locationName}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{line.quantityToShip}</TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                max={line.quantityToShip}
                                value={line.quantityShipped}
                                onChange={(e) => handleQuantityChange(line.pickLineId, e.target.value)}
                                disabled={!line.isSelected}
                                className="w-20 text-right"
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={line.packageNumber?.toString() || "unassigned"}
                                onValueChange={(value) =>
                                  handleAssignToPackage(
                                    line.pickLineId,
                                    value === "unassigned" ? undefined : parseInt(value)
                                  )
                                }
                                disabled={!line.isSelected || packages.length === 0}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Assign..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unassigned">Unassigned</SelectItem>
                                  {packages.map(pkg => (
                                    <SelectItem key={pkg.packageNumber} value={pkg.packageNumber.toString()}>
                                      Package {pkg.packageNumber}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, filteredLines.length)} of{" "}
                    {filteredLines.length} items
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => navigateTo("sales-orders")}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Creating Shipment...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Create Shipment
                </>
              )}
            </Button>
          </div>
        </>
      )}

      {/* Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? `Edit Package ${editingPackage.packageNumber}` : "Add Package"}
            </DialogTitle>
            <DialogDescription>
              Enter package dimensions and tracking information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="package-weight">Weight *</Label>
                <div className="flex gap-2">
                  <Input
                    id="package-weight"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={packageWeight}
                    onChange={(e) => setPackageWeight(e.target.value)}
                    className="flex-1"
                  />
                  <Select value={packageWeightUnit} onValueChange={(value: "kg" | "lbs") => setPackageWeightUnit(value)}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="lbs">lbs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="package-tracking">Tracking Number *</Label>
                <Input
                  id="package-tracking"
                  placeholder="Enter tracking number"
                  value={packageTrackingNumber}
                  onChange={(e) => setPackageTrackingNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dimensions (L × W × H)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Length"
                  value={packageLength}
                  onChange={(e) => setPackageLength(e.target.value)}
                />
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Width"
                  value={packageWidth}
                  onChange={(e) => setPackageWidth(e.target.value)}
                />
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Height"
                  value={packageHeight}
                  onChange={(e) => setPackageHeight(e.target.value)}
                />
                <Select value={packageDimensionUnit} onValueChange={(value: "cm" | "in") => setPackageDimensionUnit(value)}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cm">cm</SelectItem>
                    <SelectItem value="in">in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePackage}>
              {editingPackage ? "Update Package" : "Add Package"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Setup Sheet */}
      <Sheet open={showQuickSetup} onOpenChange={setShowQuickSetup}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Quick Setup</SheetTitle>
            <SheetDescription>
              Apply carrier and service to all items
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Carrier</Label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger>
                  <SelectValue placeholder="Select carrier..." />
                </SelectTrigger>
                <SelectContent>
                  {SHIPPING_CARRIERS.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={service} onValueChange={setService} disabled={!carrier}>
                <SelectTrigger>
                  <SelectValue placeholder="Select service..." />
                </SelectTrigger>
                <SelectContent>
                  {availableServices.map(s => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              className="w-full"
              onClick={() => handleApplyQuickSetup(carrier, service)}
              disabled={!carrier || !service}
            >
              Apply to All Items
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Shipment</DialogTitle>
            <DialogDescription>
              Please review the shipment details before confirming
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Alert>
              <PackageCheck className="h-4 w-4" />
              <AlertTitle>Shipment Summary</AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-1 text-sm">
                  <div><span className="font-medium">Order:</span> {selectedOrder?.soNumber}</div>
                  <div><span className="font-medium">Reference:</span> {shipmentReference}</div>
                  <div><span className="font-medium">Items:</span> {summaryMetrics.totalItems} items in {summaryMetrics.totalLines} lines</div>
                  <div><span className="font-medium">Packages:</span> {summaryMetrics.totalPackages} packages ({summaryMetrics.totalWeight.toFixed(2)} kg)</div>
                  <div><span className="font-medium">Carrier:</span> {SHIPPING_CARRIERS.find(c => c.code === carrier)?.name}</div>
                  <div><span className="font-medium">Service:</span> {SHIPPING_SERVICES.find(s => s.code === service)?.name}</div>
                </div>
              </AlertDescription>
            </Alert>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                This action will create a shipment record and update stock levels. This cannot be undone.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmShipment}>
              Confirm Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
